import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { money } from "@/dealership/lib/api";
import { computeTotals } from "@/dealership/lib/totals";
import { toast } from "sonner";
import PageHeader from "@/dealership/components/PageHeader";
import DeskDealButton from "@/dealership/components/desk/DeskDealButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, FileText, Pencil, Trash2, X } from "lucide-react";

const empty = {
  vehicle_id: "", lead_id: "",
  customer_name: "", customer_address: "", customer_city: "", customer_province: "ON",
  customer_postal: "", customer_phone: "", customer_email: "", customer_license: "",
  vehicle_desc: "", vehicle_vin: "", vehicle_year: "", vehicle_make: "", vehicle_model: "", vehicle_odometer: "",
  vehicle_trim: "", vehicle_color: "", vehicle_int_color: "", vehicle_type: "", vehicle_pkg: "",
  insurance_company: "", insurance_policy: "", insurance_exp: "",
  finance_company: "", finance_amount: "", finance_apr: "", finance_term: "", finance_frequency: "", finance_payment: "", finance_first_payment: "",
  service_plan: "",
  sale_price: "", warranty_name: "", warranty_price: "", warranty_cost: "", admin_fee: "", safety_cert_fee: "",
  finance_reserve: "",
  backend_income: [], additional_costs: [],
  other_fees: [], trade_in_value: "", trade_in_vin: "", trade_in_desc: "", lien_amount: "",
  licensing_fee: "", deposit: "", salesperson: "", deal_date: new Date().toISOString().slice(0, 10),
  tax_exempt: false,
  lender_funding_status: "Not financed", funded_date: "",
  status: "Deal Closed", notes: "",
};

const DEAL_STATUSES = ["Pending", "Deal Closed", "Paid", "Delivered"];
const dealStatusStyle = {
  "Pending": "bg-zinc-500/12 text-zinc-400",
  "Deal Closed": "bg-amber-500/12 text-amber-400",
  "Paid": "bg-blue-500/12 text-blue-400",
  "Delivered": "bg-green-500/12 text-green-400",
};
const fmtStatusDate = (d) => {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return ""; }
};

export default function Deals() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const { data: deals = [], isLoading } = useQuery({
    queryKey: ["deals"],
    queryFn: async () => (await api.get("/deals")).data,
  });
  const { data: vehicles = [] } = useQuery({
    queryKey: ["inventory"],
    queryFn: async () => (await api.get("/inventory")).data,
  });

  useEffect(() => {
    const eid = searchParams.get("edit");
    if (eid && deals.length) {
      const d = deals.find((x) => x.id === eid);
      if (d) { openEdit(d); setSearchParams({}, { replace: true }); }
    }
  }, [searchParams, deals]); // eslint-disable-line

  const saveMut = useMutation({
    mutationFn: async (f) => {
      const numKeys = ["sale_price", "warranty_price", "warranty_cost", "admin_fee", "safety_cert_fee",
        "finance_reserve", "trade_in_value", "lien_amount", "licensing_fee", "deposit", "vehicle_year", "vehicle_odometer",
        "finance_amount", "finance_payment"];
      const body = { ...f };
      numKeys.forEach((k) => (body[k] = f[k] === "" ? (k.startsWith("vehicle") ? null : 0) : Number(f[k])));
      body.other_fees = f.other_fees.map((x) => ({ label: x.label, amount: Number(x.amount || 0), taxable: x.taxable !== false }));
      body.backend_income = (f.backend_income || []).filter((x) => x.label || x.amount).map((x) => ({ label: x.label, amount: Number(x.amount || 0) }));
      body.additional_costs = (f.additional_costs || []).filter((x) => x.label || x.amount).map((x) => ({ label: x.label, amount: Number(x.amount || 0) }));
      if (editing) return (await api.put(`/deals/${editing}`, body)).data;
      return (await api.post("/deals", body)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.invalidateQueries({ queryKey: ["inventory"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success(editing ? "Deal updated" : "Deal created");
      setOpen(false);
    },
    onError: () => toast.error("Failed to save deal"),
  });

  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/deals/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deals"] });
      qc.invalidateQueries({ queryKey: ["inventory"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      toast.success("Deal deleted");
    },
  });

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (d) => {
    setEditing(d.id);
    const f = { ...empty, ...d };
    Object.keys(f).forEach((k) => { if (f[k] == null) f[k] = ""; });
    f.other_fees = (d.other_fees || []).map((x) => ({ ...x }));
    f.backend_income = (d.backend_income || []).map((x) => ({ ...x }));
    f.additional_costs = (d.additional_costs || []).map((x) => ({ ...x }));
    setForm(f);
    setOpen(true);
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSelectVehicle = (id) => {
    const v = vehicles.find((x) => x.id === id);
    if (v) {
      setForm((f) => ({
        ...f, vehicle_id: id,
        vehicle_desc: `${v.year} ${v.make} ${v.model} ${v.trim || ""}`.trim(),
        vehicle_vin: v.vin, vehicle_year: v.year, vehicle_make: v.make,
        vehicle_model: v.model, vehicle_odometer: v.odometer,
        vehicle_trim: f.vehicle_trim || v.trim || "",
        vehicle_color: f.vehicle_color || v.color || "",
        vehicle_type: f.vehicle_type || v.body_type || "",
        sale_price: f.sale_price || v.listing_price || "",
      }));
    } else {
      set("vehicle_id", id);
    }
  };

  const addFee = () => set("other_fees", [...form.other_fees, { label: "", amount: "", taxable: true }]);
  const updFee = (i, k, v) => {
    const arr = [...form.other_fees]; arr[i] = { ...arr[i], [k]: v }; set("other_fees", arr);
  };
  const rmFee = (i) => set("other_fees", form.other_fees.filter((_, idx) => idx !== i));

  const addLine = (key) => set(key, [...(form[key] || []), { label: "", amount: "" }]);
  const updLine = (key, i, k, v) => { const arr = [...(form[key] || [])]; arr[i] = { ...arr[i], [k]: v }; set(key, arr); };
  const rmLine = (key, i) => set(key, (form[key] || []).filter((_, idx) => idx !== i));

  const totals = computeTotals(form);

  return (
    <div className="p-8">
      <PageHeader
        title="Deals & Bills of Sale"
        subtitle="Build OMVIC-compliant bills of sale with HST, warranty and trade-in handling."
        action={
          <Button onClick={openNew} data-testid="add-deal-button">
            <Plus className="h-4 w-4 mr-2" /> New Deal
          </Button>
        }
      />

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Bill #</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Vehicle</TableHead>
              <TableHead className="text-right">Sale Price</TableHead>
              <TableHead className="text-right">HST</TableHead>
              <TableHead className="text-right">Total</TableHead>
              <TableHead className="text-right">Paid</TableHead>
              <TableHead className="text-right">Balance Due</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={10} className="text-center py-10 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : deals.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center py-10 text-muted-foreground">No deals yet. Create your first bill of sale.</TableCell></TableRow>
            ) : (
              deals.map((d) => (
                <TableRow key={d.id} data-testid={`deal-row-${d.id}`} className="cursor-pointer" onClick={() => navigate(`/dealership/deals/${d.id}`)}>
                  <TableCell className="font-mono-data text-xs">{d.bill_number}</TableCell>
                  <TableCell className="font-semibold">{d.customer_name}</TableCell>
                  <TableCell className="text-sm">{d.vehicle_desc || "—"}</TableCell>
                  <TableCell className="text-right font-mono-data">{money(d.sale_price)}</TableCell>
                  <TableCell className="text-right font-mono-data">
                    {d.totals.tax_exempt
                      ? <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-500/12 border border-red-500/25 text-red-400" data-testid={`deal-tax-exempt-${d.id}`}>Exempt</span>
                      : money(d.totals.hst)}
                  </TableCell>
                  <TableCell className="text-right font-mono-data font-semibold">{money(d.totals.total)}</TableCell>
                  <TableCell className="text-right font-mono-data" data-testid={`deal-paid-${d.id}`}>{money(d.balance?.total_paid)}</TableCell>
                  <TableCell className="text-right font-mono-data font-semibold" data-testid={`deal-balance-${d.id}`}>
                    {d.balance?.paid_in_full
                      ? <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-green-500/12 border border-green-500/25 text-green-400" data-testid={`deal-paid-pill-${d.id}`}>Paid in full</span>
                      : d.balance?.overpaid
                        ? <span className="text-[#16A34A]">Credit {money(Math.abs(d.balance.balance_due))}</span>
                        : <span className="text-primary">{money(d.balance?.balance_due)}</span>}
                  </TableCell>
                  <TableCell>
                    <Badge className={`border-0 ${dealStatusStyle[d.status] || "bg-zinc-500/12 text-zinc-400"}`}>{d.status}</Badge>
                    {d.status_changed_at && <div className="text-[11px] text-muted-foreground mt-1" data-testid={`deal-status-date-${d.id}`}>Updated {fmtStatusDate(d.status_changed_at)}</div>}
                  </TableCell>
                  <TableCell className="text-right">
                    <DeskDealButton dealId={d.id} iconOnly label="Desk this deal" testid={`desk-deal-${d.id}`} />
                    <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); navigate(`/dealership/bill/${d.id}`); }} data-testid={`bill-deal-${d.id}`} title="View Bill of Sale"><FileText className="h-4 w-4 text-primary" /></Button>
                    <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); openEdit(d); }} data-testid={`edit-deal-${d.id}`}><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="icon" onClick={(e) => { e.stopPropagation(); delMut.mutate(d.id); }} data-testid={`delete-deal-${d.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit Deal" : "New Deal"}</DialogTitle>
          </DialogHeader>

          <div className="grid lg:grid-cols-3 gap-6 py-2">
            <div className="lg:col-span-2 space-y-5">
              {/* Vehicle */}
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-3">Vehicle</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2 space-y-2">
                    <Label className="label-mono">Select from Inventory</Label>
                    <Select value={form.vehicle_id} onValueChange={onSelectVehicle}>
                      <SelectTrigger data-testid="deal-vehicle-select"><SelectValue placeholder="Choose a unit (optional)" /></SelectTrigger>
                      <SelectContent>
                        {vehicles.map((v) => (
                          <SelectItem key={v.id} value={v.id}>{v.year} {v.make} {v.model} · {v.stock_number}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2 space-y-2"><Label className="label-mono">Vehicle Description</Label><Input value={form.vehicle_desc} onChange={(e) => set("vehicle_desc", e.target.value)} data-testid="deal-vehicle-desc" /></div>
                  <div className="space-y-2"><Label className="label-mono">VIN</Label><Input value={form.vehicle_vin} onChange={(e) => set("vehicle_vin", e.target.value)} className="font-mono-data" /></div>
                  <div className="space-y-2"><Label className="label-mono">Odometer (km)</Label><Input type="number" value={form.vehicle_odometer} onChange={(e) => set("vehicle_odometer", e.target.value)} /></div>
                  <div className="space-y-2"><Label className="label-mono">Type</Label><Input value={form.vehicle_type} onChange={(e) => set("vehicle_type", e.target.value)} placeholder="e.g. Sedan / SUV" /></div>
                  <div className="space-y-2"><Label className="label-mono">Trim</Label><Input value={form.vehicle_trim} onChange={(e) => set("vehicle_trim", e.target.value)} /></div>
                  <div className="space-y-2"><Label className="label-mono">Package</Label><Input value={form.vehicle_pkg} onChange={(e) => set("vehicle_pkg", e.target.value)} /></div>
                  <div className="space-y-2"><Label className="label-mono">Exterior Color</Label><Input value={form.vehicle_color} onChange={(e) => set("vehicle_color", e.target.value)} /></div>
                  <div className="space-y-2"><Label className="label-mono">Interior Color</Label><Input value={form.vehicle_int_color} onChange={(e) => set("vehicle_int_color", e.target.value)} /></div>
                </div>
              </div>

              {/* Insurance */}
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-3">Insurance</p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2"><Label className="label-mono">Insurance Co.</Label><Input value={form.insurance_company} onChange={(e) => set("insurance_company", e.target.value)} /></div>
                  <div className="space-y-2"><Label className="label-mono">Policy No.</Label><Input value={form.insurance_policy} onChange={(e) => set("insurance_policy", e.target.value)} /></div>
                  <div className="space-y-2"><Label className="label-mono">Exp. Date</Label><Input type="date" value={form.insurance_exp} onChange={(e) => set("insurance_exp", e.target.value)} /></div>
                </div>
              </div>

              {/* Finance Terms */}
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-3">Finance Terms</p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2"><Label className="label-mono">Lender</Label><Input value={form.finance_company} onChange={(e) => set("finance_company", e.target.value)} /></div>
                  <div className="space-y-2"><Label className="label-mono">Amount Financed</Label><Input type="number" value={form.finance_amount} onChange={(e) => set("finance_amount", e.target.value)} /></div>
                  <div className="space-y-2"><Label className="label-mono">APR (%)</Label><Input value={form.finance_apr} onChange={(e) => set("finance_apr", e.target.value)} /></div>
                  <div className="space-y-2"><Label className="label-mono">Term</Label><Input value={form.finance_term} onChange={(e) => set("finance_term", e.target.value)} placeholder="e.g. 60 mo" /></div>
                  <div className="space-y-2"><Label className="label-mono">Frequency</Label>
                    <Select value={form.finance_frequency} onValueChange={(v) => set("finance_frequency", v)}>
                      <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Weekly">Weekly</SelectItem>
                        <SelectItem value="Bi-weekly">Bi-weekly</SelectItem>
                        <SelectItem value="Monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label className="label-mono">Payment</Label><Input type="number" value={form.finance_payment} onChange={(e) => set("finance_payment", e.target.value)} /></div>
                  <div className="space-y-2"><Label className="label-mono">First Payment</Label><Input type="date" value={form.finance_first_payment} onChange={(e) => set("finance_first_payment", e.target.value)} /></div>
                  <div className="space-y-2"><Label className="label-mono">Funding Status</Label>
                    <Select value={form.lender_funding_status || "Not financed"} onValueChange={(v) => set("lender_funding_status", v)}>
                      <SelectTrigger data-testid="deal-funding-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Not financed">Not financed</SelectItem>
                        <SelectItem value="Pending">Pending</SelectItem>
                        <SelectItem value="Funded">Funded</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2"><Label className="label-mono">Funded Date</Label><Input type="date" value={form.funded_date} onChange={(e) => set("funded_date", e.target.value)} data-testid="deal-funded-date" /></div>
                  <div className="space-y-2"><Label className="label-mono">Service Plan</Label>
                    <Select value={form.service_plan} onValueChange={(v) => set("service_plan", v)}>
                      <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Yes">Yes</SelectItem>
                        <SelectItem value="No">No</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Customer */}
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-3">Purchaser</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label className="label-mono">Name *</Label><Input value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} data-testid="deal-customer-name" /></div>
                  <div className="space-y-2"><Label className="label-mono">Driver's License</Label><Input value={form.customer_license} onChange={(e) => set("customer_license", e.target.value)} /></div>
                  <div className="col-span-2 space-y-2"><Label className="label-mono">Address</Label><Input value={form.customer_address} onChange={(e) => set("customer_address", e.target.value)} /></div>
                  <div className="space-y-2"><Label className="label-mono">City</Label><Input value={form.customer_city} onChange={(e) => set("customer_city", e.target.value)} /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-2"><Label className="label-mono">Prov.</Label><Input value={form.customer_province} onChange={(e) => set("customer_province", e.target.value)} /></div>
                    <div className="space-y-2"><Label className="label-mono">Postal</Label><Input value={form.customer_postal} onChange={(e) => set("customer_postal", e.target.value)} /></div>
                  </div>
                  <div className="space-y-2"><Label className="label-mono">Phone</Label><Input value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} /></div>
                  <div className="space-y-2"><Label className="label-mono">Email</Label><Input value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} /></div>
                </div>
              </div>

              {/* Pricing */}
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-3">Pricing & Add-ons</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label className="label-mono">Sale Price *</Label><Input type="number" value={form.sale_price} onChange={(e) => set("sale_price", e.target.value)} data-testid="deal-sale-price" /></div>
                  <div className="space-y-2"><Label className="label-mono">Safety Cert Fee</Label><Input type="number" value={form.safety_cert_fee} onChange={(e) => set("safety_cert_fee", e.target.value)} /></div>
                  <div className="space-y-2"><Label className="label-mono">Warranty Name</Label><Input value={form.warranty_name} onChange={(e) => set("warranty_name", e.target.value)} placeholder="e.g. 36mo Powertrain" /></div>
                  <div className="space-y-2"><Label className="label-mono">Warranty Price</Label><Input type="number" value={form.warranty_price} onChange={(e) => set("warranty_price", e.target.value)} data-testid="deal-warranty-price" /></div>
                  <div className="space-y-2"><Label className="label-mono">Admin Fee</Label><Input type="number" value={form.admin_fee} onChange={(e) => set("admin_fee", e.target.value)} /></div>
                  <div className="space-y-2"><Label className="label-mono">Finance Reserve (lender pays you)</Label><Input type="number" value={form.finance_reserve} onChange={(e) => set("finance_reserve", e.target.value)} data-testid="deal-finance-reserve" /></div>
                  <div className="space-y-2"><Label className="label-mono">Warranty Cost (you pay)</Label><Input type="number" value={form.warranty_cost} onChange={(e) => set("warranty_cost", e.target.value)} data-testid="deal-warranty-cost" /></div>
                  <div className="space-y-2"><Label className="label-mono">Licensing Fee (tax-exempt)</Label><Input type="number" value={form.licensing_fee} onChange={(e) => set("licensing_fee", e.target.value)} /></div>
                </div>
                {/* HST on / off — some deals (status Indian, export, dealer-to-dealer) carry no tax */}
                <div className="mt-4 flex items-center justify-between gap-4 rounded-2xl border border-border bg-white/[0.02] px-4 py-3" data-testid="deal-tax-toggle-row">
                  <div className="min-w-0">
                    <Label className="label-mono">Charge HST (13%)</Label>
                    <p className="text-xs text-muted-foreground mt-1">
                      {form.tax_exempt
                        ? "Tax exempt — no HST will be charged or reported on this deal."
                        : "HST is charged on the net taxable amount."}
                    </p>
                  </div>
                  <div className="flex items-center gap-2.5 shrink-0">
                    <span className={`text-xs ${form.tax_exempt ? "text-red-400" : "text-muted-foreground"}`}>No</span>
                    <Switch
                      checked={!form.tax_exempt}
                      onCheckedChange={(on) => set("tax_exempt", !on)}
                      data-testid="deal-tax-switch"
                      aria-label="Charge HST"
                    />
                    <span className={`text-xs ${form.tax_exempt ? "text-muted-foreground" : "text-primary"}`}>Yes</span>
                  </div>
                </div>
                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="label-mono">Other Fees</Label>
                    <Button type="button" variant="outline" size="sm" onClick={addFee}><Plus className="h-3 w-3 mr-1" />Add fee</Button>
                  </div>
                  {form.other_fees.map((fe, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <Input placeholder="Label" value={fe.label} onChange={(e) => updFee(i, "label", e.target.value)} className="flex-1" />
                      <Input type="number" placeholder="Amount" value={fe.amount} onChange={(e) => updFee(i, "amount", e.target.value)} className="w-28" />
                      <label className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
                        <input type="checkbox" checked={fe.taxable !== false} onChange={(e) => updFee(i, "taxable", e.target.checked)} /> taxable
                      </label>
                      <Button type="button" variant="ghost" size="icon" onClick={() => rmFee(i)}><X className="h-4 w-4" /></Button>
                    </div>
                  ))}
                </div>
                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="label-mono">Back-end Income <span className="text-muted-foreground font-normal">(extra revenue → counts as profit)</span></Label>
                    <Button type="button" variant="outline" size="sm" onClick={() => addLine("backend_income")} data-testid="deal-add-income"><Plus className="h-3 w-3 mr-1" />Add income</Button>
                  </div>
                  {(form.backend_income || []).map((fe, i) => (
                    <div key={i} className="flex gap-2 items-center" data-testid={`deal-income-row-${i}`}>
                      <Input placeholder="Label (e.g. GAP, accessories)" value={fe.label} onChange={(e) => updLine("backend_income", i, "label", e.target.value)} className="flex-1" />
                      <Input type="number" placeholder="Amount" value={fe.amount} onChange={(e) => updLine("backend_income", i, "amount", e.target.value)} className="w-28" data-testid={`deal-income-amount-${i}`} />
                      <Button type="button" variant="ghost" size="icon" onClick={() => rmLine("backend_income", i)}><X className="h-4 w-4" /></Button>
                    </div>
                  ))}
                </div>
                <div className="mt-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="label-mono">Additional Costs <span className="text-muted-foreground font-normal">(extra costs → subtracted from profit)</span></Label>
                    <Button type="button" variant="outline" size="sm" onClick={() => addLine("additional_costs")} data-testid="deal-add-cost"><Plus className="h-3 w-3 mr-1" />Add cost</Button>
                  </div>
                  {(form.additional_costs || []).map((fe, i) => (
                    <div key={i} className="flex gap-2 items-center" data-testid={`deal-cost-row-${i}`}>
                      <Input placeholder="Label (e.g. detailing, freight)" value={fe.label} onChange={(e) => updLine("additional_costs", i, "label", e.target.value)} className="flex-1" />
                      <Input type="number" placeholder="Amount" value={fe.amount} onChange={(e) => updLine("additional_costs", i, "amount", e.target.value)} className="w-28" data-testid={`deal-cost-amount-${i}`} />
                      <Button type="button" variant="ghost" size="icon" onClick={() => rmLine("additional_costs", i)}><X className="h-4 w-4" /></Button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Trade-in */}
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mb-3">Trade-in & Payment</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label className="label-mono">Trade-in Allowance</Label><Input type="number" value={form.trade_in_value} onChange={(e) => set("trade_in_value", e.target.value)} data-testid="deal-tradein" /></div>
                  <div className="space-y-2"><Label className="label-mono">Lien on Trade</Label><Input type="number" value={form.lien_amount} onChange={(e) => set("lien_amount", e.target.value)} /></div>
                  <div className="col-span-2 space-y-2"><Label className="label-mono">Trade-in Description</Label><Input value={form.trade_in_desc} onChange={(e) => set("trade_in_desc", e.target.value)} /></div>
                  <div className="space-y-2"><Label className="label-mono">Deposit</Label><Input type="number" value={form.deposit} onChange={(e) => set("deposit", e.target.value)} data-testid="deal-deposit" /></div>
                  <div className="space-y-2"><Label className="label-mono">Salesperson</Label><Input value={form.salesperson} onChange={(e) => set("salesperson", e.target.value)} /></div>
                  <div className="space-y-2"><Label className="label-mono">Deal Date</Label><Input type="date" value={form.deal_date} onChange={(e) => set("deal_date", e.target.value)} /></div>
                  <div className="space-y-2"><Label className="label-mono">Status</Label>
                    <Select value={form.status} onValueChange={(v) => set("status", v)}>
                      <SelectTrigger data-testid="deal-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DEAL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            {/* Totals sidebar */}
            <div className="lg:col-span-1">
              <Card className="p-5 sticky top-2 bg-secondary/40">
                <h4 className="font-heading font-bold mb-4">Deal Summary</h4>
                <div className="space-y-2 text-sm font-mono-data">
                  <Row l="Taxable Subtotal" v={money(totals.taxable_subtotal)} />
                  {totals.trade_in_value > 0 && <Row l="Less Trade-in" v={`- ${money(totals.trade_in_value)}`} />}
                  <Row l="Net Taxable" v={money(totals.taxable_net)} />
                  <Row l={totals.tax_exempt ? "HST (exempt)" : "HST (13%)"} v={totals.tax_exempt ? "No tax" : money(totals.hst)} />
                  {totals.non_taxable_fees > 0 && <Row l="Non-tax Fees" v={money(totals.non_taxable_fees)} />}
                  {totals.licensing_fee > 0 && <Row l="Licensing" v={money(totals.licensing_fee)} />}
                  <div className="border-t border-border pt-2 mt-2">
                    <Row l="Total" v={money(totals.total)} bold />
                  </div>
                  {totals.deposit > 0 && <Row l="Deposit" v={`- ${money(totals.deposit)}`} />}
                  <Row l="Balance Due" v={money(totals.balance_due)} bold accent />
                </div>
              </Card>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => saveMut.mutate(form)} disabled={!form.customer_name || !form.sale_price || saveMut.isPending} data-testid="save-deal-button">
              {editing ? "Update Deal" : "Create Deal"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ l, v, bold, accent }) {
  return (
    <div className="flex justify-between">
      <span className={`${bold ? "font-semibold" : "text-muted-foreground"}`}>{l}</span>
      <span className={`${bold ? "font-bold" : ""} ${accent ? "text-primary" : ""}`}>{v}</span>
    </div>
  );
}
